
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

export interface ModeleNavire {
  id: string;
  nom_constructeur: string;
  nom_modele: string;
  annee: number | null;
  annee_construction: number | null;
  longueur: number | null;
  largeur: number | null;
  moteur: string | null;
  capacite_carburant: number | null;
  capacite_eau: number | null;
  capacite_eaux_usees: number | null;
}

export const useModelesNavires = () => {
  const [modeles, setModeles] = useState<ModeleNavire[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchModeles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('modeles_navires')
        .select('*')
        .order('nom_constructeur', { ascending: true })
        .order('nom_modele', { ascending: true });

      if (error) {
        console.error('Erreur lors du chargement des modèles:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les modèles de navires.",
          variant: "destructive",
        });
      } else {
        console.log(`${data?.length || 0} modèles chargés depuis la base de données`);
        setModeles(data || []);
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModeles();
  }, []);

  const getConstructeurs = () => {
    const constructeurs = [...new Set(modeles.map(m => m.nom_constructeur))];
    return constructeurs.sort();
  };

  const getModelesByConstructeur = (constructeur: string) => {
    return modeles.filter(m => m.nom_constructeur === constructeur)
      .sort((a, b) => a.nom_modele.localeCompare(b.nom_modele));
  };

  const getModelesByYear = (year: number) => {
    return modeles.filter(m => m.annee === year || m.annee_construction === year);
  };

  const searchModeles = (searchTerm: string) => {
    const term = searchTerm.toLowerCase();
    return modeles.filter(m => 
      m.nom_constructeur.toLowerCase().includes(term) ||
      m.nom_modele.toLowerCase().includes(term) ||
      m.moteur?.toLowerCase().includes(term)
    );
  };

  return {
    modeles,
    loading,
    getConstructeurs,
    getModelesByConstructeur,
    getModelesByYear,
    searchModeles,
    refetch: fetchModeles
  };
};
